import { Component, OnInit, OnDestroy } from '@angular/core';
import { FornecedoresService, FornecedorListDto } from '../fornecedores.service';
import { CategoryService } from '../../categories/category.service';
import { NotificationService } from '../../../core/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-fornecedores-list',
  template: `
    <div>
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-serif text-text-default">Fornecedores</h2>
        <a routerLink="/admin/fornecedores/new" class="bg-primary text-white px-4 py-2 rounded-full">Criar Fornecedor</a>
      </div>

      <div class="mb-4 flex gap-3 items-center">
        <div class="flex items-center w-full md:w-1/2 gap-2">
          <div class="flex-1 flex">
            <input #q type="search" placeholder="Pesquisar por nome ou cidade" (input)="onSearch($any($event.target).value)" (keyup.enter)="onSearchNow(q.value)" class="flex-1 border border-border rounded-l px-3 py-2" />
            <button type="button" (click)="onSearchNow(q.value)" class="px-3 py-2 bg-primary text-white rounded-r">Pesquisar</button>
          </div>
          <div>
            <select (change)="onCategoryChange($any($event.target).value)" class="border border-border rounded px-2 py-1">
              <option value="">Todas categorias</option>
              <option *ngFor="let c of categories" [value]="c.id">{{ c.nome }}</option>
            </select>
          </div>
        </div>
        <select (change)="onPageSize($any($event.target).value)" class="border border-border rounded px-2 py-1">
          <option value="12">12</option>
          <option value="24">24</option>
          <option value="48">48</option>
        </select>
      </div>

      <div *ngIf="data.length === 0">
        <app-empty-state title="Nenhum fornecedor encontrado">
          <a routerLink="/admin/fornecedores/new" class="mt-3 inline-block bg-primary text-white px-4 py-2 rounded">Criar o primeiro</a>
        </app-empty-state>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <app-fornecedor-card *ngFor="let f of data" [fornecedor]="f" (delete)="onDelete($event)" (toggleActiveEvent)="onToggleActive($event)" (resetLeadCountEvent)="onResetLeadCount($event)"></app-fornecedor-card>
      </div>

      <div class="mt-6 flex justify-center">
        <app-pagination [page]="page" [pageSize]="pageSize" [total]="total" (pageChange)="onPageChange($event)"></app-pagination>
      </div>
    </div>
  `
})
export class FornecedoresListComponent implements OnInit, OnDestroy {
  data: FornecedorListDto[] = [];
  page = 1;
  pageSize = 12;
  total = 0;
  categories: any[] = [];
  selectedCategory?: string;
  currentQuery?: string;

  private search$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(private svc: FornecedoresService, private catSvc: CategoryService, private notification: NotificationService) {}

  ngOnInit(): void {
    this.load();

    const s = this.search$.pipe(debounceTime(400), distinctUntilChanged()).subscribe(raw => {
      const q = (raw || '').toString().trim();
      this.currentQuery = q || undefined;
      this.page = 1;
      this.load();
    });
    this.subs.push(s);

    // load categories for filter
    this.catSvc.listAll().subscribe((cats: any) => {
      this.categories = (cats && cats.data) ? cats.data : (Array.isArray(cats) ? cats : []);
    }, err => {
      console.error('[FornecedoresList] failed to load categories', err);
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearch(q: string) { this.currentQuery = (q || '').toString().trim() || undefined; this.search$.next(q); }

  onSearchNow(q: string) {
    const value = (q || '').toString().trim();
    this.page = 1;
    this.currentQuery = value || undefined;
    this.load();
  }

  onCategoryChange(id: string) {
    this.selectedCategory = id || undefined;
    this.page = 1;
    this.load();
  }

  onPageSize(v: any) { this.pageSize = Number(v); this.page = 1; this.load(); }

  onPageChange(p: number) { this.page = p; this.load(); }

  load(q?: string) {
    const term = q ?? this.currentQuery;
    const filters: any = {};
    if (term) {
      // send only `nome` for search to avoid redundant params
      filters.nome = term;
    }
    // category filter: send a single well-known param to avoid duplicated params
    if (this.selectedCategory) {
      // backend currently expects `categoriaId` for filtering fornecedores by category
      filters.categoriaId = this.selectedCategory;
    }
    this.svc.list(this.page, this.pageSize, filters).subscribe((res: any) => {
      const items = res.items || res.data || [];
      // Normalize media/thumbnail for each fornecedor so the card can render a primary image.
      this.data = items.map((f: any) => {
        // Prefer explicit `primaryImage` returned by the API
        const primaryImg = f.primaryImage || f.primary_image;
        if (primaryImg) {
          const thumbnail = { id: primaryImg.id || primaryImg.Id, url: primaryImg.url || primaryImg.Url, isPrimary: primaryImg.isPrimary ?? primaryImg.IsPrimary };
          return { ...f, thumbnail };
        }

        // Fallback to medias array shapes (imagens field from backend)
        const medias = f.imagens || f.medias || (f.medias?.data) || (f.media) || [];
        const primary = (medias || []).find((m: any) => m.isPrimary === true || m.IsPrimary === true) || (medias && medias[0]);
        const thumbnail = primary ? { id: primary.id || primary.Id, url: primary.url || primary.Url, isPrimary: primary.isPrimary ?? primary.IsPrimary } : undefined;
        return { ...f, thumbnail };
      });
      this.total = res.total || res.meta?.total || (items.length || 0);
    });
  }

  onDelete(id: string) {
    this.svc.delete(id).subscribe({
      next: () => {
        this.notification.showSuccess('Fornecedor excluído com sucesso');
        this.data = this.data.filter(f => f.id !== id);
        this.total = Math.max(0, this.total - 1);
      },
      error: (err: any) => {
        console.error('[FornecedoresList] erro ao excluir fornecedor', err);
        this.notification.showError('Erro ao excluir fornecedor');
      }
    });
  }

  onToggleActive(id: string) {
    this.svc.toggleActive(id).subscribe({
      next: () => {
        this.notification.showSuccess('Status atualizado com sucesso');
        this.load(); // Reload to reflect changes
      },
      error: (err: any) => {
        console.error('[FornecedoresList] erro ao alterar status', err);
        this.notification.showError('Erro ao alterar status do fornecedor');
      }
    });
  }

  onResetLeadCount(id: string) {
    this.svc.resetLeadCount(id).subscribe({
      next: () => {
        this.notification.showSuccess('Contador de leads resetado');
        this.load(); // Reload to reflect changes
      },
      error: (err: any) => {
        console.error('[FornecedoresList] erro ao resetar contador', err);
        this.notification.showError('Erro ao resetar contador de leads');
      }
    });
  }
}
